import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Database, 
  Plus, 
  Search, 
  Filter, 
  BookOpen, 
  Copy, 
  Edit, 
  Trash2,
  Tag,
  Star,
  StarOff,
  FolderPlus,
  Folder,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { ScrollArea } from '../../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { useToast } from '../../../hooks/useToast';
import { useTenantContext } from '../../../context/TenantContext';
import { supabase } from '../../../lib/supabase';
import { QuizQuestion } from '../../../services/elearningQuizService';

interface QuestionBankItem {
  id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'single_choice' | 'true_false' | 'text_input' | 'matching' | 'ordering';
  points: number;
  explanation?: string;
  is_required: boolean;
  category_id?: string;
  tags: string[];
  is_favorite: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
  tenant_id: number;
  answers: {
    id: string;
    answer_text: string;
    is_correct: boolean;
    explanation?: string;
  }[];
}

interface QuestionCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  tenant_id: number;
  questions_count: number;
}

interface QuestionBankProps {
  isOpen: boolean;
  onClose: () => void;
  onQuestionSelected: (question: QuestionBankItem) => void;
}

const QuestionBank: React.FC<QuestionBankProps> = ({ isOpen, onClose, onQuestionSelected }) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [questions, setQuestions] = useState<QuestionBankItem[]>([]);
  const [categories, setCategories] = useState<QuestionCategory[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<QuestionBankItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // États pour la création de catégorie
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  // États pour l'édition de question
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionBankItem | null>(null);

  const questionTypes = [
    { value: 'single_choice', label: 'Choix unique' },
    { value: 'multiple_choice', label: 'Choix multiple' },
    { value: 'true_false', label: 'Vrai/Faux' },
    { value: 'text_input', label: 'Texte libre' },
    { value: 'ordering', label: 'Classement' },
    { value: 'matching', label: 'Appariement' }
  ];

  const categoryColors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'
  ];

  useEffect(() => {
    if (isOpen && tenant?.id) {
      loadQuestionBank();
      loadCategories();
    }
  }, [isOpen, tenant?.id]);

  useEffect(() => {
    filterQuestions();
  }, [questions, searchTerm, selectedCategory, selectedType, showFavoritesOnly]);

  const loadQuestionBank = async () => {
    if (!tenant?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('elearn_question_bank')
        .select(`
          *,
          elearn_question_bank_answers(*)
        `)
        .eq('tenant_id', tenant.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedQuestions = (data || []).map(question => ({
        ...question,
        answers: question.elearn_question_bank_answers || [],
        tags: question.tags || []
      }));

      setQuestions(formattedQuestions);
    } catch (error) {
      console.error('Erreur lors du chargement de la banque de questions:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger la banque de questions',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    if (!tenant?.id) return;

    try {
      const { data, error } = await supabase
        .from('elearn_question_categories')
        .select(`
          *,
          questions_count:elearn_question_bank(count)
        `)
        .eq('tenant_id', tenant.id)
        .order('name', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
    }
  };

  const filterQuestions = () => {
    let filtered = [...questions];

    // Filtre par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtre par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(q => q.category_id === selectedCategory);
    }

    // Filtre par type
    if (selectedType !== 'all') {
      filtered = filtered.filter(q => q.question_type === selectedType);
    }

    // Filtre favoris
    if (showFavoritesOnly) {
      filtered = filtered.filter(q => q.is_favorite);
    }

    setFilteredQuestions(filtered);
  };

  const createCategory = async () => {
    if (!tenant?.id || !categoryForm.name.trim()) return;

    try {
      const { data, error } = await supabase
        .from('elearn_question_categories')
        .insert({
          name: categoryForm.name,
          description: categoryForm.description,
          color: categoryForm.color,
          tenant_id: tenant.id
        })
        .select()
        .single();

      if (error) throw error;

      setCategories(prev => [...prev, { ...data, questions_count: 0 }]);
      setCategoryForm({ name: '', description: '', color: '#3B82F6' });
      setIsCategoryDialogOpen(false);

      toast({
        title: 'Catégorie créée',
        description: `La catégorie "${data.name}" a été créée avec succès.`
      });
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer la catégorie',
        variant: 'destructive'
      });
    }
  };

  const toggleFavorite = async (questionId: string) => {
    if (!tenant?.id) return;

    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    try {
      const { error } = await supabase
        .from('elearn_question_bank')
        .update({ is_favorite: !question.is_favorite })
        .eq('id', questionId)
        .eq('tenant_id', tenant.id);

      if (error) throw error;

      setQuestions(prev => 
        prev.map(q => 
          q.id === questionId 
            ? { ...q, is_favorite: !q.is_favorite }
            : q
        )
      );
    } catch (error) {
      console.error('Erreur lors de la mise à jour du favori:', error);
    }
  };

  const deleteQuestion = async (questionId: string) => {
    if (!tenant?.id) return;

    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la question "${question.question_text.substring(0, 50)}..." ?`)) {
      return;
    }

    try {
      // Supprimer d'abord les réponses
      await supabase
        .from('elearn_question_bank_answers')
        .delete()
        .eq('question_id', questionId);

      // Puis supprimer la question
      const { error } = await supabase
        .from('elearn_question_bank')
        .delete()
        .eq('id', questionId)
        .eq('tenant_id', tenant.id);

      if (error) throw error;

      setQuestions(prev => prev.filter(q => q.id !== questionId));

      toast({
        title: 'Question supprimée',
        description: 'La question a été supprimée de la banque.'
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer la question',
        variant: 'destructive'
      });
    }
  };

  const handleQuestionSelect = (question: QuestionBankItem) => {
    // Incrémenter le compteur d'utilisation
    supabase
      .from('elearn_question_bank')
      .update({ usage_count: question.usage_count + 1 })
      .eq('id', question.id)
      .eq('tenant_id', tenant?.id || 0);

    onQuestionSelected(question);
    onClose();
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'single_choice': return '○';
      case 'multiple_choice': return '☐';
      case 'true_false': return '✓';
      case 'text_input': return '✎';
      case 'ordering': return '↕';
      case 'matching': return '⟷';
      default: return '?';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center">
            <Database className="w-6 h-6 mr-2 text-blue-400" />
            Banque de Questions
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-h-[75vh] overflow-hidden">
          {/* Sidebar - Filtres et catégories */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-sm flex items-center justify-between">
                  <span>Filtres</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                      setSelectedType('all');
                      setShowFavoritesOnly(false);
                    }}
                    className="text-gray-400 hover:text-white h-6 px-2"
                  >
                    <X size={12} />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white h-8"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 text-xs">Type</Label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les types</SelectItem>
                      {questionTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {getQuestionTypeIcon(type.value)} {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-300 text-xs">Catégorie</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les catégories</SelectItem>
                      {categories.map(category => (
                        <SelectItem key={category.id} value={category.id}>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: category.color }}
                            />
                            <span>{category.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="favorites"
                    checked={showFavoritesOnly}
                    onCheckedChange={setShowFavoritesOnly}
                  />
                  <Label htmlFor="favorites" className="text-gray-300 text-xs">
                    Favoris uniquement
                  </Label>
                </div>
              </CardContent>
            </Card>

            {/* Catégories */}
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-sm flex items-center justify-between">
                  <span>Catégories</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsCategoryDialogOpen(true)}
                    className="text-blue-400 hover:text-blue-300 h-6 px-2"
                  >
                    <Plus size={12} />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {categories.map(category => (
                    <div
                      key={category.id}
                      className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                        selectedCategory === category.id 
                          ? 'bg-blue-500/20 border border-blue-500/30' 
                          : 'hover:bg-gray-700/50'
                      }`}
                      onClick={() => setSelectedCategory(category.id)}
                    >
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="text-white text-xs">{category.name}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {category.questions_count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Liste des questions */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <div className="text-gray-400 text-sm">
                {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} trouvée{filteredQuestions.length !== 1 ? 's' : ''}
              </div>
            </div>

            <ScrollArea className="h-[60vh]">
              <div className="space-y-3">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                    <p className="text-gray-400">Chargement...</p>
                  </div>
                ) : filteredQuestions.length === 0 ? (
                  <div className="text-center py-12">
                    <Database className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">Aucune question trouvée</h3>
                    <p className="text-gray-400 mb-4">
                      {questions.length === 0 
                        ? 'Votre banque de questions est vide. Créez vos premières questions depuis l\'éditeur de quiz.'
                        : 'Aucune question ne correspond à vos critères de recherche.'
                      }
                    </p>
                  </div>
                ) : (
                  <AnimatePresence>
                    {filteredQuestions.map((question, index) => (
                      <motion.div
                        key={question.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="bg-gray-800/50 border-gray-700 hover:border-gray-600 transition-colors">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center space-x-2">
                                <Badge variant="outline" className="border-blue-500/30 text-blue-400 bg-blue-500/10 text-xs">
                                  {getQuestionTypeIcon(question.question_type)} {questionTypes.find(t => t.value === question.question_type)?.label}
                                </Badge>
                                <Badge variant="outline" className="border-purple-500/30 text-purple-400 bg-purple-500/10 text-xs">
                                  {question.points} pts
                                </Badge>
                                {question.usage_count > 0 && (
                                  <Badge variant="outline" className="border-green-500/30 text-green-400 bg-green-500/10 text-xs">
                                    Utilisée {question.usage_count}x
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center space-x-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleFavorite(question.id)}
                                  className={`h-8 w-8 p-0 ${question.is_favorite ? 'text-yellow-400' : 'text-gray-400'}`}
                                >
                                  {question.is_favorite ? <Star size={14} /> : <StarOff size={14} />}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleQuestionSelect(question)}
                                  className="text-blue-400 hover:text-blue-300 h-8 w-8 p-0"
                                >
                                  <Copy size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteQuestion(question.id)}
                                  className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>

                            <h4 className="text-white font-medium mb-2 leading-relaxed">
                              {question.question_text.length > 150 
                                ? `${question.question_text.substring(0, 150)}...` 
                                : question.question_text}
                            </h4>

                            <div className="flex items-center justify-between text-xs text-gray-400">
                              <div className="flex items-center space-x-4">
                                <span>{question.answers.length} réponse{question.answers.length !== 1 ? 's' : ''}</span>
                                <span>{question.answers.filter(a => a.is_correct).length} correcte{question.answers.filter(a => a.is_correct).length !== 1 ? 's' : ''}</span>
                              </div>
                              <span>{new Date(question.created_at).toLocaleDateString('fr-FR')}</span>
                            </div>

                            {question.tags.length > 0 && (
                              <div className="flex items-center space-x-1 mt-2">
                                <Tag size={12} className="text-gray-400" />
                                <div className="flex flex-wrap gap-1">
                                  {question.tags.map((tag, tagIndex) => (
                                    <Badge key={tagIndex} variant="outline" className="text-xs border-gray-600 text-gray-400">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Dialog de création de catégorie */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent className="bg-black/90 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Nouvelle Catégorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="category-name">Nom de la catégorie</Label>
              <Input
                id="category-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Ex: Mathématiques, Histoire..."
              />
            </div>
            <div>
              <Label htmlFor="category-description">Description (optionnel)</Label>
              <Textarea
                id="category-description"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Description de la catégorie..."
                rows={2}
              />
            </div>
            <div>
              <Label>Couleur</Label>
              <div className="flex space-x-2 mt-2">
                {categoryColors.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${
                      categoryForm.color === color ? 'border-white' : 'border-gray-600'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setCategoryForm(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCategoryDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={createCategory} disabled={!categoryForm.name.trim()}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export { QuestionBank };
